import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { adminApi, levelApi } from '@/utils/api';
import { useNotify } from '@/context/NotificationContext';
import { useHapticFeedback } from '@/hooks/useHapticFeedback';
import { Search, Gift, Package, Sparkles, User, Check, X, CreditCard, ChevronDown, Image } from 'lucide-react';

interface UserResult {
    user_id: string;
    telegram_id: string;
    username: string;
    first_name: string;
    xp: number;
    available_balance: number;
}

export const GiftManager = () => {
    const { showNotification } = useNotify();
    const { impactOccurred } = useHapticFeedback();

    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState<UserResult[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const [selectedUser, setSelectedUser] = useState<UserResult | null>(null);

    const [giftMode, setGiftMode] = useState<'subscription' | 'card'>('subscription');
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Subscriptions
    const [levels, setLevels] = useState<any[]>([]);
    const [selectedTier, setSelectedTier] = useState<string>('');
    const [months, setMonths] = useState<number>(1);

    // Cards
    const [cardAnalytics, setCardAnalytics] = useState<any[]>([]);
    const [selectedCards, setSelectedCards] = useState<string[]>([]);
    const [selectedPackage, setSelectedPackage] = useState<string | null>(null);

    // Global Asset List
    const [assets, setAssets] = useState<{ cards: any[], skins: any[] }>({ cards: [], skins: [] });
    const [globalItemType, setGlobalItemType] = useState<'card' | 'skin'>('card');
    const [selectedGlobalItem, setSelectedGlobalItem] = useState<any | null>(null);

    const searchDebounce = useRef<NodeJS.Timeout>();

    useEffect(() => {
        // Fetch auxiliary data
        levelApi.getAll().then(res => res.json()).then(data => setLevels(Array.isArray(data) ? data : []));
        adminApi.getCardAnalytics().then(res => res.json()).then(data => {
            if (data.success) setCardAnalytics(data.data || []);
        });
        adminApi.getAssetsList().then(res => res.json()).then(data => {
            if (data.success) setAssets({ cards: data.cards || [], skins: data.skins || [] });
        });
    }, []);

    useEffect(() => {
        if (!searchQuery.trim()) {
            setSearchResults([]);
            return;
        }

        setIsSearching(true);
        if (searchDebounce.current) clearTimeout(searchDebounce.current);

        searchDebounce.current = setTimeout(async () => {
            try {
                const res = await adminApi.searchUsers(searchQuery);
                const data = await res.json();
                if (data.success) {
                    setSearchResults(data.users || []);
                }
            } catch (e) {
                console.error("Search Error", e);
            } finally {
                setIsSearching(false);
            }
        }, 500);
    }, [searchQuery]);

    const handleGift = async () => {
        setIsSubmitting(true);
        impactOccurred('medium');

        try {
            if (!selectedUser) throw new Error("Please select a user first.");
                
            if (giftMode === 'subscription') {
                if (!selectedTier) throw new Error("Please select a subscription tier.");
                const res = await adminApi.giftSubscription({
                    targetUserId: selectedUser.user_id,
                    tierId: selectedTier,
                    months
                });
                const data = await res.json();
                if (!data.success) throw new Error(data.error);
                showNotification('Gift Sent', data.message, 'success');
            } else {
                if (selectedCards.length === 0) throw new Error("Please select at least one card to gift.");

                let successCount = 0;
                let failCount = 0;

                for (const cid of selectedCards) {
                    try {
                        const res = await adminApi.giftCard({
                            targetUserId: selectedUser.user_id,
                            cardId: cid
                        });
                        const data = await res.json();
                        if (data.success) {
                            successCount++;
                        } else {
                            failCount++;
                        }
                    } catch (err) {
                        failCount++;
                    }
                }

                if (successCount === 0 && failCount > 0) {
                    throw new Error("Failed to send any cards.");
                } else if (successCount > 0 && failCount > 0) {
                    showNotification('Partial Success', `Sent ${successCount} cards, ${failCount} failed.`, 'info');
                } else {
                    showNotification('Gift Sent', `Successfully sent ${successCount} cards!`, 'success');
                }

                // Refresh card limits
                adminApi.getCardAnalytics().then(r => r.json()).then(d => {
                    if (d.success) setCardAnalytics(d.data || []);
                });
            }

            // Success Reset
            setSelectedUser(null);
            setSearchQuery('');
            setSelectedCards([]);
        } catch (e: any) {
            showNotification('Error', e.message || "Failed to send gift", 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-pink-500 to-orange-400 flex items-center justify-center shadow-lg shadow-pink-500/20">
                    <Gift size={24} className="text-white" />
                </div>
                <div>
                    <h2 className="text-2xl font-black tracking-tight text-white">Gift Center</h2>
                    <p className="text-xs font-bold text-white/40 uppercase tracking-widest">Award Assets to Users</p>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Search Panel */}
                <div className="bg-[#111113] rounded-[32px] border border-white/5 p-6 relative overflow-hidden flex flex-col h-[500px]">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/5 rounded-full blur-[80px] pointer-events-none" />

                    <h3 className="text-sm font-bold text-white mb-4 flex items-center gap-2">
                        <Search size={16} className="text-blue-400" /> User Lookup
                    </h3>

                    <div className="relative z-10">
                        <input
                            value={selectedUser ? selectedUser.username || selectedUser.first_name : searchQuery}
                            onChange={(e) => {
                                setSearchQuery(e.target.value);
                                if (selectedUser) setSelectedUser(null);
                            }}
                            placeholder="Search by ID, @username..."
                            className="w-full bg-[#1c1c1e] border border-white/10 rounded-2xl px-5 py-4 pl-12 text-sm text-white focus:border-blue-500 focus:bg-[#202022] outline-none transition-all"
                        />
                        <div className="absolute left-4 top-[18px] text-white/40">
                            <Search size={18} />
                        </div>
                        {selectedUser && (
                            <button onClick={() => { setSelectedUser(null); setSearchQuery(''); }} className="absolute right-4 top-[18px] text-white/40 hover:text-white">
                                <X size={18} />
                            </button>
                        )}
                    </div>

                    <div className="mt-4 flex-1 overflow-y-auto space-y-2 relative z-10 custom-scrollbar pr-2">
                        {!selectedUser && isSearching && (
                            <div className="text-center py-8 text-sm text-white/40 animate-pulse font-bold tracking-widest uppercase">
                                Scanning Database...
                            </div>
                        )}
                        {!selectedUser && !isSearching && searchResults.length === 0 && searchQuery.length > 0 && (
                            <div className="text-center py-8 text-sm text-white/40 font-bold tracking-widest uppercase">
                                No Target Found
                            </div>
                        )}

                        {!selectedUser && searchResults.map(user => (
                            <div
                                key={user.user_id}
                                onClick={() => setSelectedUser(user)}
                                className="group flex items-center justify-between p-4 rounded-2xl bg-[#1c1c1e]/50 border border-white/5 hover:bg-[#1c1c1e] hover:border-blue-500/30 cursor-pointer transition-all active:scale-[0.98]"
                            >
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center text-blue-400">
                                        <User size={18} />
                                    </div>
                                    <div>
                                        <div className="font-bold text-sm text-white flex items-center gap-2">
                                            {user.first_name} {user.username && <span className="text-blue-400">@{user.username}</span>}
                                        </div>
                                        <div className="text-[10px] text-white/40 font-mono mt-0.5">ID: {user.telegram_id} • XP: {user.xp}</div>
                                    </div>
                                </div>
                                <div className="opacity-0 group-hover:opacity-100 text-blue-400 transition-opacity">
                                    <Check size={18} />
                                </div>
                            </div>
                        ))}

                        {/* Selected Layout */}
                        <AnimatePresence>
                            {selectedUser && (
                                <motion.div
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0 }}
                                    className="p-6 rounded-3xl bg-gradient-to-br from-blue-900/20 to-purple-900/20 border border-blue-500/20 text-center"
                                >
                                    <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 mx-auto flex items-center justify-center shadow-[0_0_30px_rgba(59,130,246,0.5)] mb-4">
                                        <User size={32} className="text-white" />
                                    </div>
                                    <h4 className="text-xl font-black text-white">{selectedUser.first_name}</h4>
                                    <p className="text-blue-400 font-mono text-xs mb-4">@{selectedUser.username || selectedUser.telegram_id}</p>

                                    <div className="grid grid-cols-2 gap-3 mt-6">
                                        <div className="bg-black/40 p-3 rounded-2xl border border-white/5">
                                            <div className="text-[10px] text-white/40 uppercase font-bold tracking-widest mb-1">XP Level</div>
                                            <div className="font-mono text-lg text-white">{selectedUser.xp}</div>
                                        </div>
                                        <div className="bg-black/40 p-3 rounded-2xl border border-white/5">
                                            <div className="text-[10px] text-white/40 uppercase font-bold tracking-widest mb-1">Balance</div>
                                            <div className="font-mono text-lg text-green-400">{selectedUser.available_balance || 0}</div>
                                        </div>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </div>

                {/* Gifting Panel */}
                <div className={`bg-[#111113] rounded-[32px] border ${selectedUser ? 'border-blue-500/30 shadow-[0_0_50px_rgba(59,130,246,0.1)]' : 'border-white/5 opacity-50 pointer-events-none'} p-6 relative overflow-hidden flex flex-col h-[500px] transition-all duration-300`}>
                    <div className="absolute top-0 right-0 w-64 h-64 bg-orange-500/5 rounded-full blur-[80px] pointer-events-none" />

                    <div className="flex bg-[#1c1c1e] p-1.5 rounded-2xl mb-6 relative z-10 border border-white/5">
                        <button
                            onClick={() => setGiftMode('subscription')}
                            className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${giftMode === 'subscription' ? 'bg-blue-600 text-white shadow-lg' : 'text-white/40 hover:text-white'}`}
                        >
                            <Sparkles size={14} /> Sub
                        </button>
                        <button
                            onClick={() => setGiftMode('card')}
                            className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${giftMode === 'card' ? 'bg-purple-600 text-white shadow-lg' : 'text-white/40 hover:text-white'}`}
                        >
                            <CreditCard size={14} /> Individual Card
                        </button>
                    </div>

                    <div className="flex-1 space-y-5 overflow-y-auto custom-scrollbar relative z-10 pr-2 pb-20">
                        {giftMode === 'subscription' && (
                            <>
                                <div>
                                    <label className="block text-[10px] font-bold text-white/40 uppercase tracking-widest mb-3">Select Tier</label>
                                    <div className="grid grid-cols-2 gap-3">
                                        {levels.map(tier => (
                                            <div
                                                key={tier.level_id}
                                                onClick={() => setSelectedTier(tier.level_id.toString())}
                                                className={`p-4 rounded-2xl border cursor-pointer transition-all ${selectedTier === tier.level_id.toString() ? 'bg-blue-500/10 border-blue-500' : 'bg-[#1c1c1e] border-white/5 hover:border-white/10'}`}
                                            >
                                                <div className="font-bold text-sm text-white">{tier.level_name}</div>
                                                <div className="text-[10px] text-white/40 mt-1 uppercase">Tier {tier.level_id}</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <div className="mt-6">
                                    <label className="block text-[10px] font-bold text-white/40 uppercase tracking-widest mb-3">Duration (Months)</label>
                                    <div className="flex items-center bg-[#1c1c1e] border border-white/5 rounded-2xl p-2 gap-2">
                                        {[1, 3, 6, 12].map(m => (
                                            <button
                                                key={m}
                                                onClick={() => setMonths(m)}
                                                className={`flex-1 py-3 border rounded-xl font-bold text-sm transition-all ${months === m ? 'bg-blue-600 border-blue-500 text-white' : 'bg-transparent border-transparent text-white/40'}`}
                                            >
                                                {m}M
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </>
                        )}

                        {giftMode === 'card' && (
                            <div className="flex flex-col h-full">
                                {!selectedPackage ? (
                                    <>
                                        <label className="block text-[10px] font-bold text-white/40 uppercase tracking-widest mb-3">Select Package</label>
                                        <div className="space-y-2 overflow-y-auto custom-scrollbar flex-1 pb-2">
                                            {Array.from(new Set(cardAnalytics.map(c => c.package_name || 'Standard Editions'))).map(pkg => {
                                                const pkgCards = cardAnalytics.filter(c => (c.package_name || 'Standard Editions') === pkg);
                                                return (
                                                    <div
                                                        key={pkg}
                                                        onClick={() => setSelectedPackage(pkg)}
                                                        className="flex items-center justify-between p-4 rounded-2xl bg-[#1c1c1e] border border-white/5 hover:border-blue-500/30 cursor-pointer transition-all active:scale-[0.98]"
                                                    >
                                                        <div className="flex items-center gap-3">
                                                            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500/20 to-purple-500/20 border border-purple-500/20 flex items-center justify-center overflow-hidden">
                                                                <Package size={18} className="text-purple-400" />
                                                            </div>
                                                            <div>
                                                                <div className="font-bold text-sm text-white">{pkg}</div>
                                                                <div className="text-[10px] font-mono mt-0.5 text-purple-400">
                                                                    {pkgCards.length} Cards Available
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <ChevronDown size={18} className="text-white/40 -rotate-90" />
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div className="flex items-center justify-between mb-3">
                                            <div className="flex items-center gap-2">
                                                <label className="block text-[10px] font-bold text-white/40 uppercase tracking-widest">Cards in {selectedPackage}</label>
                                                <button
                                                    onClick={() => {
                                                        const pkgCards = cardAnalytics.filter(c => (c.package_name || 'Standard Editions') === selectedPackage && !(c.max_supply > 0 && c.remaining_supply <= 0));
                                                        const allSelected = pkgCards.every(c => selectedCards.includes(c.definition_id.toString()));
                                                        if (allSelected) {
                                                            setSelectedCards(selectedCards.filter(id => !pkgCards.find(c => c.definition_id.toString() === id)));
                                                        } else {
                                                            const newSelections = new Set([...selectedCards, ...pkgCards.map(c => c.definition_id.toString())]);
                                                            setSelectedCards(Array.from(newSelections));
                                                        }
                                                    }}
                                                    className="px-2 py-0.5 rounded bg-blue-500/10 text-blue-400 text-[9px] font-bold uppercase tracking-wider hover:bg-blue-500/20 transition-colors"
                                                >
                                                    Select All
                                                </button>
                                            </div>
                                            <button onClick={() => { setSelectedPackage(null); }} className="text-[10px] text-blue-400 uppercase tracking-widest font-bold hover:text-blue-300 transition-colors">
                                                Back
                                            </button>
                                        </div>
                                        <div className="space-y-2 overflow-y-auto custom-scrollbar flex-1 pb-2">
                                            {cardAnalytics.filter(c => (c.package_name || 'Standard Editions') === selectedPackage).map(card => {
                                                const isDepleted = card.max_supply > 0 && card.remaining_supply <= 0;
                                                const isSelected = selectedCards.includes(card.definition_id.toString());
                                                return (
                                                    <div
                                                        key={card.definition_id}
                                                        onClick={() => {
                                                            if (isDepleted) return;
                                                            if (isSelected) {
                                                                setSelectedCards(selectedCards.filter(id => id !== card.definition_id.toString()));
                                                            } else {
                                                                setSelectedCards([...selectedCards, card.definition_id.toString()]);
                                                            }
                                                        }}
                                                        className={`flex items-center justify-between p-4 rounded-2xl border transition-all ${isDepleted ? 'bg-red-500/5 border-red-500/10 opacity-50 cursor-not-allowed' : isSelected ? 'bg-purple-500/10 border-purple-500 cursor-pointer shadow-[0_0_20px_rgba(168,85,247,0.1)]' : 'bg-[#1c1c1e] border-white/5 hover:border-white/10 cursor-pointer'}`}
                                                    >
                                                        <div className="flex items-center gap-3">
                                                            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center overflow-hidden relative group">
                                                                {card.url ? (
                                                                    <img src={card.url.startsWith('http') || card.url.startsWith('/') ? card.url : `/assets/cards/${card.url}`} alt={card.card_name} className="w-full h-full object-cover transition-transform group-hover:scale-110" />
                                                                ) : (
                                                                    <CreditCard size={18} className="text-white relative z-10" />
                                                                )}
                                                            </div>
                                                            <div>
                                                                <div className="font-bold text-sm text-white">{card.card_name}</div>
                                                                <div className="text-[10px] font-mono mt-0.5" style={{ color: isDepleted ? '#f87171' : '#a78bfa' }}>
                                                                    {card.max_supply > 0 ? `Stock: ${card.remaining_supply} / ${card.max_supply}` : 'Stock: Unlimited'}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        {isSelected && !isDepleted && (
                                                            <Check size={18} className="text-purple-400" />
                                                        )}
                                                        {isDepleted && (
                                                            <div className="text-[10px] text-red-500 font-bold uppercase tracking-widest">Depleted</div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="absolute bottom-6 left-6 right-6 z-20">
                        <button
                            onClick={handleGift}
                            disabled={isSubmitting || (giftMode === 'subscription' && !selectedTier) || (giftMode === 'card' && selectedCards.length === 0)}
                            className={`w-full py-4 rounded-2xl font-black uppercase tracking-widest transition-all flex items-center justify-center gap-2 ${(giftMode === 'subscription' ? !selectedTier : selectedCards.length === 0) ? 'bg-white/5 text-white/20 border border-white/5 cursor-not-allowed' :
                                    'text-white border border-white/10 shadow-[0_0_30px_rgba(0,0,0,0.5)] hover:scale-[1.02] active:scale-[0.98]'
                                } ${isSubmitting ? 'opacity-70 pointer-events-none' : ''}`}
                            style={{
                                background: ((giftMode === 'subscription' && !selectedTier) || (giftMode === 'card' && selectedCards.length === 0)) ? '' : (giftMode === 'subscription' ? 'linear-gradient(135deg, #2563eb, #7c3aed)' : 'linear-gradient(135deg, #7c3aed, #db2777)')
                            }}
                        >
                            {isSubmitting ? "Processing..." : `Issue ${giftMode === 'subscription' ? 'Subscription' : 'Master Card'}`}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
